import Requests from "@/services/Requests";

const state = {
  limit: 10,
  regions: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  allRegions: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
  createStatus: "unitiliazed", // unitiliazed, loading, success, error
  updateStatus: "unitiliazed", // unitiliazed, loading, success, error
  deleteStatus: "unitiliazed", // unitiliazed, loading, success, error
  regionById: {
    status: "unitiliazed", // unitiliazed, loading, success, error
    data: [],
    count: 0,
  },
};
const getters = {
  /**
   * Returns a subset of regions based on the given page number.
   *
   * @param {Object} state - The Vuex state object.
   * @param {number} [page=0] - The page number to filter regions by. Defaults to 0.
   * @return {Array} An array of regions filtered by the given page number. If page is 0, returns all regions.
   */
  getRegions:
    (state) =>
    (page = 0) =>
      page === 0
        ? state.regions.data
        : state.regions.data
            .filter(
              (value, index, self) =>
                index === self.findIndex((t) => t.id === value.id)
            )
            .filter((d) => d.page === page),
  getAllRegions: (state) => state.allRegions.data,
  getRegionStatus: (state) => state.regions.status,
  getRegionCount: (state) => state.regions.count,
  getRegionPage: (state) => state.regions.page,
  getRegionPages: (state) => Math.ceil(state.regions.count / state.limit),
  getRegionsLoading: (state) => state.regions.status === "loading",
  getRegionById: (state) => (id) =>
    state.regionById.data.find((d) => d.id === id) || {},
};
const mutations = {
  /**
   * Updates the status of the region in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the region.
   */
  SET_REGION_STATUS(state, payload) {
    console.log(state.regions, "status: ", payload);
    state.regions.status = payload;
  },
  /**
   * Sets the regions and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of regions.
   * @param {number} payload.count - The total count of regions.
   */
  SET_REGIONS(state, payload) {
    state.regions.data = payload.items;
    state.regions.count = payload.count;
  },
  /**
   * Sets the regions and count in the state based on the payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the items and count.
   * @param {Array} payload.items - The array of regions.
   * @param {number} payload.count - The total count of regions.
   */
  SET_ALL_REGIONS(state, payload) {
    console.log("SET_ALL_REGIONS", payload);
    state.allRegions.data = payload.items;
    state.allRegions.count = payload.count;
  },
  /**
   * Updates the status of the update region in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the region.
   */
  SET_UPDATE_REGION_STATUS(state, payload) {
    state.updateStatus = payload;
  },
  /**
   * Updates an region object in the state with new information.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The updated region object.
   * @return {void}
   */
  SET_UPDATE_REGION(state, payload) {
    const index = state.regions.data.findIndex((d) => d.id === payload.id);
    const region = state.regions.data[index];
    state.regions.data[index] = { ...region, ...payload };
  },
  SET_CREATE_REGION_STATUS(state, payload) {
    state.createStatus = payload;
  },
  /**
   * Adds a new region to the list of regions in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The region object to be added.
   * @return {void}
   */
  SET_CREATE_REGION(state, payload) {
    state.regions.data.push(payload);
  },
  /**
   * Updates the status of the delete region in the state.
   *
   * @param {Object} state - The Vuex state object.
   * @param {"unitiliazed" | "loading" | "success" | "error"} payload - The new status of the region.
   */
  SET_DELETE_REGION_STATUS(state, payload) {
    state.deleteStatus = payload;
  },
  /**
   * Removes an region from the state based on the provided payload.
   *
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the region ID to be deleted.
   * @param {number} payload.id - The ID of the region to be deleted.
   * @return {void}
   */
  SET_DELETE_REGION(state, payload) {
    state.regions.data = state.regions.data.filter((d) => d.id !== payload.id);
  },
  SET_REGION_BY_ID_STATUS(state, payload) {
    state.regionById.status = payload;
  },
  SET_REGION_BY_ID(state, payload) {
    state.regionById.data.push(payload);
  },
};

const actions = {
  /**
   * Retrieves a list of regions based on the provided payload.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} state - The Vuex state object.
   * @param {Object} payload - The payload containing the query parameters.
   * @param {Object} payload.query - The query parameters.
   * @param {string} payload.query.searchKey - The search key.
   * @param {string} payload.query.regionId - The region ID.
   * @param {string} payload.query.companyId - The company ID.
   * @param {number} payload.query.page - The page number.
   * @return {Promise<void>}
   */
  async getRegions({ state, commit }, payload) {
    // if query param is not in payload or if there is already data with same page number, don't fetch again
    if (
      !["search"].some((key) => payload.query[key]) &&
      state.regions.data.filter((region) => region.page === payload.query.page)
        .length > 0 &&
      !payload.force
    )
      return;
    const status = state.regions.status;
    commit("SET_REGION_STATUS", "loading");
    const response = await Requests.Region.Details(payload);
    if (response.message === "OK") {
      commit("SET_REGIONS", {
        items: response.data.items.map((item) => ({
          ...item,
          page: payload.query.page,
        })),
        count: response.data.count,
      });
      if (status === "unitiliazed")
        commit("SET_ALL_REGIONS", {
          items: response.data.items.map((item) => ({
            ...item,
            page: payload.query.page,
          })),
          count: response.data.count,
        });
    }
    commit("SET_REGION_STATUS", "success");
    return response.message;
  },
  /**
   * Creates an region by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the region data.
   * @param {string} payload.fullName - The full name of the region.
   * @param {string} payload.phoneNumber - The phone number of the region.
   * @param {string} payload.email - The email of the region.
   * @param {string} payload.regionId - The ID of the region the region belongs to.
   * @param {File} payload.image - The image of the region.
   * @param {number} payload.workStartTime - The start time of the work day.
   * @param {number} payload.workEndTime - The end time of the work day.
   * @return {Promise<void>} - A promise that resolves when the creation is complete.
   */
  async createRegion({ state, commit }, payload) {
    state.createStatus = "loading";
    try {
      const response = await Requests.Region.Create(payload);
      state.createStatus = "success";
      if (response.message === "OK")
        commit("SET_CREATE_REGION", {
          id: response.data,
          fullName: payload.fullName,
          phoneNumber: payload.phoneNumber,
          email: payload.email,
          regionId: payload.regionId,
          image: payload.image,
          workStartTime: payload.workStartTime,
          workEndTime: payload.workEndTime,
          page: 1,
        });
      else state.createStatus = "error";
    } catch {
      state.createStatus = "error";
    }
  },
  /**
   * Updates an region by making a request to the server.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the region data to update.
   * @param {string} payload.fullName - The full name of the region.
   * @param {string} payload.phoneNumber - The phone number of the region.
   * @param {string} payload.email - The email of the region.
   * @param {string} payload.regionId - The ID of the region the region belongs to.
   * @param {File} payload.image - The image of the region.
   * @param {number} payload.workStartTime - The start time of the work day.
   * @param {number} payload.workEndTime - The end time of the work day.
   * @param {boolean} payload.isActive - Whether the region is active or not.
   * @return {Promise<void>} - A promise that resolves when the update is complete.
   */
  async updateRegion({ state, commit }, payload) {
    state.updateStatus = "loading";
    try {
      const response = await Requests.Region.Update(payload, {
        params: { id: payload.id },
      });
      if (response.message === "OK") {
        commit("SET_UPDATE_REGION", payload);
        state.updateStatus = "success";
      } else {
        state.updateStatus = "error";
      }
      return response.message;
    } catch {
      state.updateStatus = "error";
      return "FAIL";
    }
  },
  /**
   * Removes an region from the state and updates the delete status.
   *
   * @param {Object} context - The Vuex action context.
   * @param {Object} payload - The payload containing the region data.
   * @param {number} payload.id - The ID of the region to be removed.
   * @returns {Promise<void>} A promise that resolves when the region is successfully removed.
   */
  removeRegion({ state }, { id }) {
    state.deleteStatus = "loading";

    return Requests.Region.Delete({ id })
      .then((response) => {
        state.deleteStatus = response.message === "OK" ? "success" : "error";
      })
      .catch(() => {
        state.deleteStatus = "error";
      });
  },
  /**
   * Retrieves an region by their ID and updates the region by ID status in the Vuex store.
   *
   * @param {Object} commit - The Vuex commit function.
   * @param {Object} id - The ID of the region to retrieve.
   * @return {Promise} A promise that resolves when the region is successfully retrieved and the status is updated.
   */
  getRegionById({ commit, state }, { id }) {
    // check if region already exists in the state;
    if (state.regionById.data.find((region) => region.id === id))
      return state.regionById.data.find((region) => region.id === id);
    commit("SET_REGION_BY_ID_STATUS", "loading");
    return Requests.Region.GetById({ params: { id } })
      .then((response) => {
        commit("SET_REGION_BY_ID", response.data);
        commit("SET_REGION_BY_ID_STATUS", "success");
        return response.data;
      })
      .catch(() => {
        commit("SET_REGION_BY_ID_STATUS", "error");
        return {};
      });
  },
};

export default { state, getters, mutations, actions };
